<template>
  <div class="block">
    <button
      @click="toggleDropdown"
      class="cursor-pointer relative p-1 group"
      :class="[!notification?.active ? 'text-bhav-blue-200' : 'text-bhav-blue-500']"
      :title="!notification?.active ? 'Add Notification' : 'Manage Notification Settings'"
      @mouseenter="isIconHover = true"
      @mouseleave="isIconHover = false"
    >
      <span class="fak fa-notification text-3xl group-hover:transform group-hover:-rotate-6"></span>
      <div class="absolute right-1 -top-1 flex items-center justify-center text-white rounded-full aspect-square h-5" :class="[!notification?.active ? 'bg-bhav-blue-500' : 'bg-bhav-green-600']"><span class="fas text-[8px]"
        :class="[{'fa-plus' : !notification?.active, 'fa-check' : notification.active && !isIconHover, 'fa-gear' : isIconHover}]"></span></div>
      
    </button>

    <RightDrawer :open="isOpen" title="Manage notification" @close="isOpen = false">
      <template #default="{close}">
        <div class="bg-bhav-cool-gray-50 px-4 py-2 rounded-lg flex justify-between items-center flex-0">
          <label for="option-notify" class="text-bhav-blue-300 flex-1 cursor-pointer">{{ context?.name ?? 'Community' }}</label>
          <span class="bg-bhav-blue-50 rounded-lg p-2 items-center justify-center">
            <input id="option-notify" type="checkbox" :checked="notification.active" @change="subscribe" class="appearance-none text-bhav-blue-500 fak cursor-pointer flex-0" :class="[notification.active ? 'fa-notification' : 'fa-notification-off']" />
          </span>
        </div>
        <ul class="px-5 mt-2 flex-0">
          <li v-for="option in availableOptions" :key="option.key" class="flex items-center justify-between border-l border-l-bhav-blue-50 py-3 pl-3">
            <label :for="`option-${option.key}`" class="text-sm font-medium text-bhav-blue-500 cursor-pointer flex-1">{{ option.label }}</label>
            <input :id="`option-${option.key}`" type="checkbox" :checked="option.enabled" @change="toggle(option.key, !option.enabled)" class="appearance-none text-bhav-blue-400 bg-bhav-cool-gray-50 hover:bg-white rounded-lg flex h-6 w-6 min-w-6 min-h-6 p-1 items-center justify-center cursor-pointer flex-0" :class="{'fak fa-check' : option.enabled}" />
          </li>
        </ul>
        <nav class="flex flex-col justify-end flex-1">
          <FormButton :to="{name: 'profile-tab', params: {tab: 'notification-settings'}}" class="w-full justify-center" color="blue">View All Notifications</FormButton>
        </nav>
      </template>
    </RightDrawer>
    
  </div>
</template>

<script>
import ToggleButton from './form/ToggleButton.vue'
import { NotificationsController } from '@/controllers/notifications'
import Notification from '@/models/notification'
import NotificationTypes from '@/enums/NotificationTypes'
import FormButton from './form/FormButton.vue'
import RightDrawer from './RightDrawer.vue'

export default {
  name: 'NotificationSubscription',
  components: {
    ToggleButton,
    FormButton,
    RightDrawer
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => NotificationTypes.getValidTypes().includes(value)
    },
    context: {
      type: Object,
      required: false
    },
    id: {
      type: [String, Number],
      required: true
    },
    source: {
      type: [Object, Boolean],
      default: false,
      required: false
    }
  },
  data() {
    return {
      isOpen: false,
      notificationStates: {},
      notification: new Notification,
      isLoading: false,
      isIconHover: false,
      name: null
    }
  },
  computed: {
    availableOptions() {
      const allowedMeta = NotificationTypes.getAllowedMeta(this.type)
      const allTypes = NotificationTypes.getAllTypes()

      // Create options map for all types including 'self'
      const allOptions = {}
      allTypes.forEach(type => {
        const key = type.key
        allOptions[key] = {
          key,
          label: type.label
        }
      })

      return allowedMeta.map(key => ({
        ...allOptions[key],
        enabled: this.notification?.meta?.[key] || false
      }))
    },
    hasNotifications() {
      return this.availableOptions.some(option => option.enabled)
    }
  },
  methods: {
    toggleDropdown() {
      if(this.isOpen === false && !this.notification.active) this.subscribe(); // This will cause the notification subscription to activate upon clicking the `bell +` icon
      this.isOpen = !this.isOpen;
    },
    load() {
      try {
        this.isLoading = true
        NotificationsController.index(this.type, this.context.id)
        .then(resp => {
          this.notification = resp.id ? resp : new Notification({
            notifiable_type: this.type,
            notifiable_id: this.context.id
          });
        });
      } finally {
        this.isLoading = false
      }
    },
    subscribe() {
      if(!this.notification.any) {
        this.notification.all();
      }
      if(this.notification.active) {
        this.delete();
      } else if(this.notification.id) {
        this.restore();
      } else {
        this.store();
      }
    },
    delete() {
      this.notification.none();
      NotificationsController.delete(this.notification)
      .then(() => {
        this.notification.deleted_at = new Date().toDateString()
      });
    },
    restore() {
      NotificationsController.restore(this.notification)
      .then(resp => {
        this.notification = resp
      });
    },
    store() {
      NotificationsController.store(this.notification)
      .then(resp => {
        this.notification = resp
      });
    },
    update() {
      NotificationsController.update(this.notification)
      .then(resp => {
        this.notification = resp
      });
    },
    toggle(key, status) {
      try {
        this.notification.meta[key] = status

        if(status === false && !Object.values(this.notification.meta).some(value => value === true) || status === true && !this.notification.active) {
          // If either there are no true values left or the first true value is being selected, toggle the subscription state
          this.subscribe()
        } else if (this.notification.id) {
          this.update()
        } else {
          this.store()
        }

      } catch (error) {
        console.error('Failed to save notification:', error)
      }
    }
  },
  created() {
    if(this.source === false) {
      this.load()
    } else if(this.source?.id ?? false) {
      this.notification = this.source;
    }
  }
}
</script>